import { toLocaleStringWithCurrency } from 'utils/helperNumbersFormating'

const defineDefaultCardNames: defineDefaultCardNamesTypes = {
    settlementAvailabilityInDays: 'Acquiring.SettlementAvailability',
    withdrawFeePercentages: 'Acquiring.WithdrawFeePercentage',
    refundPenaltyFee: 'Acquiring.RefundPenaltyFee',
    chargeBackPenaltyFee: 'Acquiring.ChargeBackPenaltyFee',
    rollingReserveDays: 'Acquiring.RollingReserveDays',
    rollingReservePercentage: 'Acquiring.RollingReservePercentage',
    chargeExpirationInSeconds: 'Acquiring.ChargeExpirationInSeconds',
    cardChargeTryFee: 'Acquiring.CardChargeTryFee',
    kycThreshold: 'Acquiring.KycThreshold',
    maxAmount: 'Acquiring.MaxChargeAmount',
    processingFees: 'Acquiring.ProcessingFees',
    chargeRetryCount: 'Acquiring.ChargeRetryCount',
    name: 'Acquiring.Name',
    code: 'Acquiring.Code',
    webHookUrl: 'Acquiring.webHookUrl',
    isEnabled: 'Acquiring.isEnabled',
}

const defineDefaultCardValues: defineDefaultCardValuesTypes = {
    settlementAvailabilityInDays: (value: number) => `${value} days`,
    withdrawFeePercentages: (value: Array<IWithdrawFeePercentages>) =>
        value.map((v: IWithdrawFeePercentages) => `${v.currencyCode} - ${v.amount}%`).join(`, `),
    refundPenaltyFee: (value: number) => `${value} EUR, ${value} USD`,
    chargeBackPenaltyFee: (value: number) => `${value} EUR, ${value} USD`,
    rollingReserveDays: (value: number) => `${value} days`,
    rollingReservePercentage: (value: number) => `${value} %`,
    chargeExpirationInSeconds: (value: number) => `${Math.floor(value / 60)} minutes`,
    cardChargeTryFee: (value: number) => `${value} EUR`,
    kycThreshold: (value: number) => `${value} EUR, ${value} USD`,
    maxAmount: (value: number) => `${value} EUR, ${value} USD`,
    // cardPaymentSettings: value => `${value}`,
    processingFees: value => value.map(item => `${item.amount}% ${item.currencyCode}`).join(', '),
    chargeRetryCount: (value: number) => `${value}`,
    name: value => `${value}`,
    code: value => `${value}`,
    webHookUrl: value => `${value}`,
    isEnabled: value => `${value}`,
}

export const parseAcquiringDefaultValues = (data: DefaultCardAcquiringTypes): Array<DefaultCardValue> => {
    return Object.entries(data).reduce((acc, [key, value]) => {
        if (['name', 'code', 'webHookUrl', 'isEnabled', 'cardPaymentSettings'].indexOf(key) === -1) {
            acc.push({ name: defineDefaultCardNames[key], value: defineDefaultCardValues[key](value) })
        }
        if (key === 'cardPaymentSettings') {
            Object.keys(value).map(k => {
                if (!!defineDefaultCardNames[k] && !!defineDefaultCardValues[k]) {
                    acc.push({ name: defineDefaultCardNames[k], value: defineDefaultCardValues[k](value[k]) })
                }
            })
        }
        return acc
    }, [] as Array<DefaultCardValue>)
}

export const parseTableData = (values: IRecords): ISettlementsRecordsParsed => {
    const tableRecords = values?.records?.reduce((acc, cur) => {
        const { settledAt, periodFrom, settlements, id } = cur
        return [
            ...acc,
            {
                id,
                settledAt,
                periodFrom,
                totalCharges: getTotalCharges(settlements),
                totalRefunds: getTotalRefunds(settlements),
                totalChargeBacks: getTotalChargeBacks(settlements),
                finalAmount: getFinalAmount(settlements),
                settlementsDataByCurrency: settlementsDataByCurrency(settlements),
            },
        ]
    }, [] as Array<ParsedRecord>)
    return { records: tableRecords, hasMore: values?.hasMore }
}

const getTotalCharges = (settlements: Array<Settlement>) => {
    return settlements.reduce((acc, cur) => {
        const {
            settledAmounts: {
                charges: { count },
            },
        } = cur
        return (acc += count)
    }, 0)
}

const getTotalRefunds = (settlements: Array<Settlement>) => {
    return settlements.reduce((acc, cur) => {
        const {
            extraFees: {
                refunds: { count },
            },
        } = cur
        return (acc += count)
    }, 0)
}

const getTotalChargeBacks = (settlements: Array<Settlement>) => {
    return settlements.reduce((acc, cur) => {
        const {
            extraFees: {
                chargeBacks: { count },
            },
        } = cur
        return (acc += count)
    }, 0)
}

const getFinalAmount = (settlements: Array<Settlement>) => {
    const showSlash = i => (i < settlements.length - 1 ? `/ ` : '')

    return settlements.reduce((acc, cur, i) => {
        const { finalAmount, currencyCode } = cur
        return (acc += `${toLocaleStringWithCurrency(finalAmount, currencyCode)} ${showSlash(i)}`)
    }, '' as string)
}

const defineSettlementQuantity: { [key: string]: (value: any) => string } = {
    charges: (value: Charges) => `${value.count}`,
    processingFee: (value: ProcessingFee) => `${value.percentage}%`,
    rollingReserveAllocatedVolume: (value: number) => '',
    refunds: (value: Refunds) => `${value.count}`,
    chargeBacks: (value: ChargeBacks) => `${value.count}`,
    cardCharges: (value: CardCharges) => `${value.count}`,
    finalAmount: (value: number) => '',
}

const defineSettlementVolume: { [key: string]: (value: any, currencyCode?: string) => string } = {
    charges: (value: Charges, currencyCode?: string) => toLocaleStringWithCurrency(value.totalVolume, currencyCode),
    processingFee: (value: ProcessingFee, currencyCode?: string) => toLocaleStringWithCurrency(value.totalVolume, currencyCode),
    rollingReserveAllocatedVolume: (value: number, currencyCode?: string) => toLocaleStringWithCurrency(value, currencyCode),
    refunds: (value: Refunds, currencyCode?: string) =>
        `${toLocaleStringWithCurrency(value.totalVolume, currencyCode)} (Penalty fee: ${toLocaleStringWithCurrency(
            value.penaltyFee,
            currencyCode
        )}) `,
    chargeBacks: (value: ChargeBacks, currencyCode?: string) =>
        `${toLocaleStringWithCurrency(value.totalVolume, currencyCode)}  (Penalty fee: ${toLocaleStringWithCurrency(
            value.penaltyFee,
            currencyCode
        )}) `,
    cardCharges: (value: CardCharges, currencyCode?: string) =>
        `${toLocaleStringWithCurrency(value.totalVolume, currencyCode)}  (Try fee: ${toLocaleStringWithCurrency(
            value.totalVolume,
            currencyCode
        )}) `,
    finalAmount: (value: ChargeBacks, currencyCode?: string) => `${toLocaleStringWithCurrency(value, currencyCode)}`,
}

const defineSettlementName: { [key: string]: string } = {
    charges: `Charges`,
    processingFee: 'Processing fee',
    rollingReserveAllocatedVolume: 'Allocated rolling reserve',
    refunds: 'Refunds',
    chargeBacks: 'Charge backs',
    cardCharges: 'Card charge try fee',
    finalAmount: 'Final amount',
}

const settlementsDataByCurrency = (settlements: Array<Settlement>): Array<SettlementTableData> => {
    return settlements.reduce((acc, cur) => {
        const { currencyCode, settledAmounts, extraFees, finalAmount } = cur
        const mappedProcessingFee: MappedProcessingFee = mapProcessingFee(settledAmounts)
        const flatteredData: FlatteredSettledAndExtraFees = { ...mappedProcessingFee, ...settledAmounts, ...extraFees, finalAmount }
        return [
            ...acc,
            {
                currencyCode,
                data: settlementData(flatteredData, currencyCode),
                additionalData: additionalData(flatteredData, currencyCode),
            },
        ]
    }, [] as Array<SettlementTableData>)
}

const mapProcessingFee = (settledAmounts: SettlementAmounts): MappedProcessingFee => {
    return {
        processingFee: {
            totalVolume: settledAmounts.processingFeeVolume,
            percentage: settledAmounts.processingFeePercentage,
        },
    }
}

const settlementData = (flatteredData: FlatteredSettledAndExtraFees, currencyCode: string) => {
    return Object.entries(flatteredData).reduce((cur, [key, value]) => {
        if (!defineSettlementName[key]) return cur

        return [...cur, [defineSettlementName[key], defineSettlementQuantity[key](value), defineSettlementVolume[key](value, currencyCode)]]
    }, [] as Array<Array<string>>)
}

const additionalData = (flatteredData: FlatteredSettledAndExtraFees, currencyCode: string): SettlementTableAdditionalData => {
    return {
        currencyCode,
        rollingReserveReleasedVolume: {
            name: 'Released rolling reserve',
            value: `${toLocaleStringWithCurrency(flatteredData.rollingReserveReleasedVolume, currencyCode)}`,
        },
        settledVolume: {
            name: 'Settlement amount',
            value: `${toLocaleStringWithCurrency(flatteredData.settledVolume, currencyCode)}`,
        },
        finalAmount: {
            name: 'Final amount',
            value: `${toLocaleStringWithCurrency(flatteredData.finalAmount, currencyCode)}`,
        },
    }
}

const statusDates = (data: ChargesRecord): Record<ICardPaymentStatus, string> => ({
    Initiated: data.initiatedOn,
    Paid: data.paidOn,
    Refunded: data.refundedOn,
    ChargeBacked: data.chargeBackedOn,
    Abandoned: data.abandonedOn,
    Declined: data.abandonedOn,
})

const tableValues = (data: ChargesRecord) => [
    {
        name: 'Client Email',
        value: data.email,
    },
    {
        name: 'Card last 4 digits',
        value: data.paymentCard?.last4Digits,
    },
    {
        name: 'Card expiration',
        value: data.paymentCard?.expiryDate,
    },
    {
        name: 'External ID',
        value: data.externalId,
    },
]

const chargesAdditionalData = (data: ChargesRecord) => ({
    currencyCode: data.amount?.currencyCode,
    leftSide: {
        name: 'Amount',
        value: `${toLocaleStringWithCurrency(data.amount?.value, data.amount?.currencyCode)}`,
    },
    rightSide: {
        name: 'Rolling reserve',
        value: `${toLocaleStringWithCurrency(0, 'EUR')}`,
    },
})

export const chargeDetailsTableValues = (data: ChargesRecord) => ({
    status: data.status,
    statusDates: statusDates(data),
    tableValues: tableValues(data),
    additionalData: chargesAdditionalData(data),
})
